<script setup lang="ts">
import { PropType, ref, watchEffect } from 'vue';
import { ISelectItem } from '../../../stores/core/models';
import { multiselectPreset } from '@/presets/customizations/multiselect';

const emit = defineEmits(['update:value']);
const props = defineProps({
  value: {
    type: Array as PropType<string[]>,
  },
  items: {
    type: Array as PropType<ISelectItem[]>,
  },
  maxSelectedLabels: {
    type: Number,
    default: 3,
  },
});

const selectedItems = ref([] as ISelectItem[]);

const onSelectionChanged = () => emit('update:value', selectedItems.value);

const init = () => {
  if (props.value && props.items) {
    selectedItems.value = props.items.filter((x) => props.value?.indexOf(x.value) !== -1).map((x) => x.value) || [];
  } else {
    selectedItems.value = [];
  }
};

init();

watchEffect(() => {
  // reset when filter type changes
  if (props.value === undefined) selectedItems.value = [];
});
</script>
<template>
  <MultiSelect
    filter
    class="w-full"
    display="comma"
    :options="items"
    optionLabel="label"
    option-value="value"
    v-model="selectedItems"
    :placeholder="$t('core.selectValuesPlaceholder')"
    :maxSelectedLabels="maxSelectedLabels"
    @update:model-value="onSelectionChanged"
    :ptOptions="{ mergeSections: true, mergeProps: true }"
    :pt="multiselectPreset"
  />
</template>

<style>
/* Base checkbox styles */
[data-pc-name='pcoptioncheckbox'] [data-pc-section='box'] {
  width: 20px !important;
  height: 20px !important;
  border-width: 1px !important;
}

/* Dark mode base styles */
.dark [data-pc-name='pcoptioncheckbox'] [data-pc-section='box'] {
  border-width: 1px !important;
  background-color: #1d2939 !important;
}

/* Checkmark styling */
[data-pc-name='pcoptioncheckbox'] [data-pc-section='box'] svg {
  color: #155eef !important;
  stroke-width: 1px !important;
  stroke: currentColor !important;
  fill: currentColor !important;
}

/* Checked state styles */
[data-pc-name='pcrowcheckbox'][data-p-checked='true'] [data-pc-section='box'],
[data-pc-name='pcoptioncheckbox'][data-p-checked='true'] [data-pc-section='box'] {
  background-color: #e5e7eb; /* bg-gray-200 */
  border-color: #374151 !important;
}

/* Dark mode checked state */
.dark [data-pc-name='pcrowcheckbox'][data-p-checked='true'] [data-pc-section='box'],
.dark [data-pc-name='pcoptioncheckbox'][data-p-checked='true'] [data-pc-section='box'] {
  background-color: #4b5563 !important; /* bg-gray-600 */
  border-color: #9ca3af !important;
}

/* Ensure checkmark remains visible in all states */
[data-pc-name='pcrowcheckbox'][data-p-checked='true'] [data-pc-section='box'] svg,
[data-pc-name='pcoptioncheckbox'][data-p-checked='true'] [data-pc-section='box'] svg {
  color: #155eef !important; /* Blue checkmark */
}

/* Dark Ensure checkmark remains visible in all states */
.dark [data-pc-name='pcrowcheckbox'][data-p-checked='true'] [data-pc-section='box'] svg,
.dark [data-pc-name='pcoptioncheckbox'][data-p-checked='true'] [data-pc-section='box'] svg {
  color: #99C3E6 !important; /* Blue checkmark */
}

input[type='checkbox']:checked:hover + .flex,
input[type='checkbox'][checked]:hover + .flex {
  background-color: #F6F8FB !important; /* gray-200 */
  border: 1px solid #374151 !important; /* Explicitly set border width and style */
}

/* Dark mode override */
.dark input[type='checkbox']:checked:hover + .flex,
.dark input[type='checkbox'][checked]:hover + .flex {
  background-color: #4b5563 !important; /* gray-600 */
}
</style>
